import React from "react";
import { Breadcrumbs as MUIBreadcrumbs, Link, Typography } from "@mui/material";
import { useLocation, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";

interface LocationState {
  fromPersonalArea?: boolean;
  fromStudentArea?: boolean;
  studentData?: unknown;
  studentName?: string;
  studentId?: unknown;
}

const BreadCrumbs = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const pathnames = location.pathname.split("/").filter((x) => x);
  const locationState = location.state as LocationState | undefined;
  
  // Check if we're on reading page and came from personal area
  const isReadingFromPersonalArea = 
    pathnames.includes("reading") && locationState?.fromPersonalArea;

  // Check if we're on student area page (came from classes management)
  const isStudentArea = pathnames.includes("student-area");

  // Check if we're on reading page and came from student area
  const isReadingFromStudentArea = 
    pathnames.includes("reading") && locationState?.fromStudentArea;



  // אם אנחנו בעמודי הניהול, לא נציג את פירורי הלחם
  if (pathnames.includes("admin")) {
    return null;
  }

  return (
    <div className="breadcrumbs-container">
      <MUIBreadcrumbs
        aria-label="breadcrumb"
        separator={
          isRTL ? (
            <NavigateBeforeIcon fontSize="small" />
          ) : (
            <NavigateNextIcon fontSize="small" />
          )
        }
      >
        {/* Home link - always visible */}
        <Link component={RouterLink} to="/home">
          {t("routes.home")}
        </Link>
        
        {/* If we're on student area, show classes management breadcrumb */}
        {isStudentArea && (
          <Link component={RouterLink} to="/classes-management">
            {t("routes.classes-management")}
          </Link>
        )}
        {isStudentArea && (
          <Typography color="text.primary">
            {t("studentArea")}
          </Typography>
        )}
        
        {/* If we're on reading page and came from student area, show student area breadcrumb */}
        {isReadingFromStudentArea && (
          <Link component={RouterLink} to="/classes-management">
            {t("routes.classes-management")}
          </Link>
        )}
        {isReadingFromStudentArea && (
          <Link component={RouterLink} to="/student-area" 
                state={{ 
                  studentData: locationState?.studentData, 
                  studentName: locationState?.studentName,
                  studentId: locationState?.studentId 
                }}>
            {t("studentArea")}
          </Link>
        )}
        {isReadingFromStudentArea && (
          <Typography color="text.primary">
            {t("routes.reading")}
          </Typography>
        )}
        
        {/* If we're on reading page and came from personal area, show personal area breadcrumb */}
        {isReadingFromPersonalArea && !isStudentArea && !isReadingFromStudentArea && (
          <Link component={RouterLink} to="/personal-area">
            {t("routes.personal-area")}
          </Link>
        )}
        
        {/* Handle other pages */}
        {pathnames.map((value, index) => {
          const last = index === pathnames.length - 1;
          const to = `/${pathnames.slice(0, index + 1).join("/")}`;

          if (value === "home") return null;
          
          // If we're in student area, don't show the regular breadcrumbs as we handle them specially
          if (isStudentArea || isReadingFromStudentArea) return null;
          
          // If we're showing personal area breadcrumb manually, skip it in the regular mapping
          if (isReadingFromPersonalArea && value === "reading") {
            return (
              <Typography color="text.primary" key={to}>
                {t(`routes.${value}`)}
              </Typography>
            );
          }

          // For other cases, if it's the current page (last), show as text, otherwise as link
          if (!isReadingFromPersonalArea && !isReadingFromStudentArea) {
            return last ? (
              <Typography color="text.primary" key={to}>
                {t(`routes.${value}`)}
              </Typography>
            ) : (
              <Link component={RouterLink} to={to} key={to}>
                {t(`routes.${value}`)}
              </Link>
            );
          }
          
          return null;
        })}
      </MUIBreadcrumbs>
    </div>
  );
};

export default BreadCrumbs;
